import _get from 'lodash/get'
import * as z from 'zod'

import { BODY_AREAS, illnessOptions } from '@flows/configs/petHealthV2/conditions'
import {
  v2PetPortrait,
  v2Text,
  v2TextWithPetName,
} from '@flows/configs/petHealthV2/steps/elements/shared'
import { saveCondition } from '@flows/configs/petHealthV2/utils'
import * as schemaUtils from '@flows/configs/shared/schemaUtils'
import type { StepConfigParams } from '@flows/configs/types'
import type { AnyStepConfiguration, FlowValues } from '@flows/core/types/stepTypes'

const condition_illness = (params: StepConfigParams) => {
  const { getTransientPath } = params.helperFunctions
  const areaPath = getTransientPath('condition.health_status_area')
  const optionsPath = getTransientPath('condition_illnesses')
  const illnessPath = getTransientPath('condition.health_status_diagnosis')

  return {
    stepContent: [
      v2PetPortrait(params),
      ...BODY_AREAS.map((area) => ({
        type: 'tag' as const,
        label: v2Text(`conditions.areas.${area}`),
        condition: { type: 'value' as const, valuePath: areaPath, expectedValue: area },
      })),
      { type: 'title', text: v2TextWithPetName(params, 'steps.condition_illness.title') },
      {
        type: 'searchableList',
        shouldTrackValues: true,
        groupBy: 'alphabetical',
        searchPlaceholder: v2Text('steps.condition_illness.searchPlaceholder'),
        name: illnessPath,
        options: optionsPath,
      },
    ],
    schema: schemaUtils.generateZodObjectFromPaths({
      [areaPath]: z.enum(BODY_AREAS),
      [illnessPath]: z.string().min(1),
    }),
    onPrepare: {
      type: 'simpleHook',
      callback: (flowValues: FlowValues) => {
        const area = z.enum(BODY_AREAS).safeParse(_get(flowValues, areaPath))
        const illnesses = area.success ? illnessOptions(area.data) : []
        const illness = _get(flowValues, illnessPath)

        return {
          formValues: {
            [optionsPath]: illnesses,
            [illnessPath]: illnesses.some(({ value }) => value === illness) ? illness : null,
          },
        }
      },
    },
    onSubmit: {
      type: 'simpleHook',
      callback: (flowValues: FlowValues) => saveCondition(params, flowValues),
    },
    subFlow: params.subFlow,
    nextStep: 'pre_existing_conditions',
  } as const satisfies AnyStepConfiguration
}

export default condition_illness
